// Read-only, best-effort per-SKU overlay for months the forecaster's real
// ingest job hasn't populated yet. Sourced from `sales_snapshot`'s daily
// "yesterday" top-5 product breakdown (the same table backing the live
// Sales page) — this is NEVER written to forecaster_sales_monthly, purely a
// display layer so a cell doesn't sit blank when we do have SOME real
// Shopify data for that SKU.
//
// Each snapshot's "yesterday" bucket is a single day's top-5 sellers.
// Aggregating that bucket across every day of a month (rather than reading
// one snapshot's rolling 7d/30d bucket) surfaces every SKU that was ever a
// top seller on any day, not just whichever 5 happened to lead the specific
// snapshot we looked at. Totals are approximate (summed from daily top-5
// snapshots, not a single exact query) — coverage is still inherently
// partial: a SKU that never once cracked a day's top 5 stays blank/missing,
// honestly.

import { aws } from './awsdb';

export async function fetchLiveSkuOverlay(months) {
  const { data, error } = await aws
    .from('sales_snapshot')
    .select('snapshot_date,payload')
    .order('snapshot_date', { ascending: true });
  if (error || !data?.length) return {};

  // One units-per-SKU map per calendar day, keyed off "yesterday" (the
  // snapshot dated D describes day D-1).
  const byDay = {};
  for (const row of data) {
    const list = row.payload?.top_products_by_period?.yesterday;
    if (!list) continue;
    const d = new Date(row.snapshot_date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() - 1);
    const day = d.toISOString().slice(0, 10);
    const perSku = (byDay[day] ??= {});
    for (const p of list) perSku[p.sku] = (perSku[p.sku] || 0) + p.units;
  }

  const targetMonths = new Set(months.filter(m => m.isClosed || m.isCurrent).map(m => m.key.slice(0, 7)));
  const overlay = {}; // sku_code -> { [month]: { units } }
  for (const [day, perSku] of Object.entries(byDay)) {
    const month = day.slice(0, 7);
    if (!targetMonths.has(month)) continue;
    for (const [sku, units] of Object.entries(perSku)) {
      if (!units) continue; // skip zero-unit noise (test SKUs etc.)
      overlay[sku] ??= {};
      overlay[sku][month] ??= { units: 0 };
      overlay[sku][month].units += units;
    }
  }
  return overlay;
}
